import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { ArrowLeft, DollarSign, TrendingDown, RotateCcw, Send } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v) => (v || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const statusColor = (s) => ({
  Active: 'bg-green-100 text-green-700',
  Created: 'bg-blue-100 text-blue-700',
  Retired: 'bg-gray-200 text-gray-600',
}[s] || 'bg-gray-100');
const today = () => new Date().toISOString().slice(0, 10);

const AssetView = () => {
  const { assetNumber } = useParams();
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const writable = canWrite('asset_master');

  const [asset, setAsset] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // 'acq' | 'ret' | 'xfer'
  const [busy, setBusy] = useState(false);
  const [acq, setAcq] = useState({ amount: '', posting_date: today(), offset_account: '', reference: '' });
  const [ret, setRet] = useState({ posting_date: today(), sale_proceeds: 0, offset_account: '', reference: '' });
  const [xfer, setXfer] = useState({ new_cost_center: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, s] = await Promise.all([
        axios.get(`${API}/assets/${assetNumber}`, { withCredentials: true }),
        axios.get(`${API}/assets/${assetNumber}/depreciation-schedule`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setAsset(a.data);
      setSchedule(s.data);
    } catch (e) {
      toast.error('Failed to load asset');
      navigate('/assets');
    } finally {
      setLoading(false);
    }
  }, [assetNumber, navigate]);

  useEffect(() => { load(); }, [load]);

  const postAcq = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/assets/${assetNumber}/acquisition`,
        { ...acq, amount: parseFloat(acq.amount) }, { withCredentials: true });
      toast.success('Acquisition posted');
      setDialog(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Acquisition failed'); }
    finally { setBusy(false); }
  };

  const postRet = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/assets/${assetNumber}/retirement`,
        { ...ret, sale_proceeds: parseFloat(ret.sale_proceeds) || 0 }, { withCredentials: true });
      toast.success('Asset retired');
      setDialog(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Retirement failed'); }
    finally { setBusy(false); }
  };

  const postXfer = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/assets/${assetNumber}/transfer`, xfer, { withCredentials: true });
      toast.success('Asset transferred');
      setDialog(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Transfer failed'); }
    finally { setBusy(false); }
  };

  if (loading || !asset) {
    return <Layout><div className="p-6 text-gray-500" data-testid="asset-view-loading">Loading asset...</div></Layout>;
  }

  const isRetired = asset.status === 'Retired';
  const nbv = isRetired ? 0 : asset.net_book_value;

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="asset-view-page">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/assets')} data-testid="asset-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" />Register
          </Button>
        </div>

        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">{asset.asset_number}</h1>
              <span className={`px-2 py-1 rounded text-xs font-medium ${statusColor(asset.status)}`} data-testid="asset-status">{asset.status}</span>
            </div>
            <p className="text-gray-600 mt-1">{asset.description}</p>
          </div>
          {writable && (
            <div className="flex gap-2 flex-wrap">
              {!isRetired && (
                <Button size="sm" onClick={() => setDialog('acq')} data-testid="acquisition-btn">
                  <DollarSign className="h-4 w-4 mr-1" />Acquisition
                </Button>
              )}
              {asset.status === 'Active' && (
                <>
                  <Button size="sm" variant="outline" onClick={() => setDialog('xfer')} data-testid="transfer-btn">
                    <Send className="h-4 w-4 mr-1" />Transfer
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setDialog('ret')} data-testid="retire-btn">
                    <RotateCcw className="h-4 w-4 mr-1" />Retire
                  </Button>
                </>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-4"><div className="text-xs text-gray-500">Acquisition Value (APC)</div><div className="text-2xl font-bold text-blue-700 mt-1" data-testid="view-apc">{money(asset.acquisition_value)}</div></Card>
          <Card className="p-4"><div className="text-xs text-gray-500">Accumulated Depreciation</div><div className="text-2xl font-bold text-orange-700 mt-1" data-testid="view-accum">{money(asset.accumulated_depreciation)}</div></Card>
          <Card className="p-4"><div className="text-xs text-gray-500">Net Book Value</div><div className="text-2xl font-bold text-green-700 mt-1" data-testid="view-nbv">{money(nbv)}</div></Card>
        </div>

        <Card className="p-5">
          <h3 className="font-semibold text-gray-900 mb-3">Master Data</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            {[
              ['Asset Class', asset.asset_class], ['Company Code', asset.company_code],
              ['Cost Center', asset.cost_center || '—'], ['Currency', asset.currency],
              ['Useful Life', `${asset.useful_life_years} yrs`], ['Method', asset.depreciation_method],
              ['Salvage Value', money(asset.salvage_value)], ['Capitalized', asset.capitalization_date || '—'],
              ['Acquisition Doc', asset.acquisition_document || '—'], ['Retirement Doc', asset.retirement_document || '—'],
              ['Last Dep. Period', asset.last_depreciation_period || '—'],
            ].map(([k, v]) => (
              <div key={k}><div className="text-gray-500 text-xs">{k}</div><div className="font-medium">{v}</div></div>
            ))}
          </div>
        </Card>

        {schedule && schedule.schedule?.length > 0 && (
          <Card className="p-0 overflow-hidden">
            <div className="p-4 border-b flex items-center gap-2">
              <TrendingDown className="h-4 w-4 text-gray-500" />
              <h3 className="font-semibold text-gray-900">Depreciation Schedule</h3>
              <span className="text-xs text-gray-500">(monthly {money(schedule.monthly_depreciation)})</span>
            </div>
            <div className="max-h-96 overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Period</TableHead>
                    <TableHead className="text-right">Depreciation</TableHead>
                    <TableHead className="text-right">Accumulated</TableHead>
                    <TableHead className="text-right">Net Book Value</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {schedule.schedule.map((r) => (
                    <TableRow key={r.period} data-testid={`sched-row-${r.period}`}>
                      <TableCell>{r.period}</TableCell>
                      <TableCell className="text-right">{money(r.depreciation)}</TableCell>
                      <TableCell className="text-right">{money(r.accumulated_depreciation)}</TableCell>
                      <TableCell className="text-right">{money(r.net_book_value)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        )}
      </div>

      {/* Acquisition dialog */}
      <Dialog open={dialog === 'acq'} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Post Acquisition</DialogTitle>
            <DialogDescription>Capitalize the asset. Posts DR Asset (APC) / CR clearing.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Amount</Label><Input type="number" data-testid="acq-amount" value={acq.amount} onChange={(e) => setAcq({ ...acq, amount: e.target.value })} /></div>
            <div><Label>Posting Date</Label><Input type="date" data-testid="acq-date" value={acq.posting_date} onChange={(e) => setAcq({ ...acq, posting_date: e.target.value })} /></div>
            <div><Label>Offset Account (optional)</Label><Input data-testid="acq-offset" value={acq.offset_account} onChange={(e) => setAcq({ ...acq, offset_account: e.target.value })} placeholder="defaults to class clearing G/L" /></div>
            <div><Label>Reference</Label><Input data-testid="acq-ref" value={acq.reference} onChange={(e) => setAcq({ ...acq, reference: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button onClick={postAcq} disabled={busy || !acq.amount || !acq.posting_date} data-testid="submit-acquisition-btn">{busy ? 'Posting...' : 'Post'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Retirement dialog */}
      <Dialog open={dialog === 'ret'} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Retire / Dispose Asset</DialogTitle>
            <DialogDescription>Removes APC & accumulated depreciation, books gain/loss vs. proceeds.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Posting Date</Label><Input type="date" data-testid="ret-date" value={ret.posting_date} onChange={(e) => setRet({ ...ret, posting_date: e.target.value })} /></div>
            <div><Label>Sale Proceeds (0 = scrap)</Label><Input type="number" data-testid="ret-proceeds" value={ret.sale_proceeds} onChange={(e) => setRet({ ...ret, sale_proceeds: e.target.value })} /></div>
            <div><Label>Proceeds Account (optional)</Label><Input data-testid="ret-offset" value={ret.offset_account} onChange={(e) => setRet({ ...ret, offset_account: e.target.value })} placeholder="defaults to class clearing G/L" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button onClick={postRet} disabled={busy} data-testid="submit-retirement-btn">{busy ? 'Posting...' : 'Retire'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer dialog */}
      <Dialog open={dialog === 'xfer'} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Transfer Asset</DialogTitle>
            <DialogDescription>Reassign the asset to a different cost center.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>New Cost Center</Label><Input data-testid="xfer-cc" value={xfer.new_cost_center} onChange={(e) => setXfer({ new_cost_center: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button onClick={postXfer} disabled={busy || !xfer.new_cost_center} data-testid="submit-transfer-btn">{busy ? 'Saving...' : 'Transfer'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default AssetView;
